import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, { createContext, useState } from "react";

export enum Severity {
  ERROR = "error",
  INFO = "info",
  SUCCESS = "success",
  WARNING = "warning",
}

export interface SnackbarType {
  open: boolean;
  message: string;
  severity: Severity;
}

interface SnackbarContextType {
  showSnackbar: ({
    message,
    severity,
  }: {
    message: string;
    severity: Severity;
  }) => void;
  closeSnackbar: (event: React.SyntheticEvent | Event, reason?: string) => void;
}

export const SnackbarContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [snackbar, setSnackbar] = useState<SnackbarType>({
    open: false,
    message: "",
    severity: Severity.INFO,
  });

  function closeSnackbar(event: React.SyntheticEvent | Event, reason?: string) {
    if (reason === "clickaway") {
      return;
    }

    setSnackbar({ ...snackbar, open: false });
  }

  function showSnackbar({
    message,
    severity,
  }: {
    message: string;
    severity: Severity;
  }) {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }

  return (
    <SnackbarContext.Provider
      value={{
        showSnackbar,
        closeSnackbar,
      }}
    >
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        key={snackbar.message}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const SnackbarContext = createContext<SnackbarContextType>({
  showSnackbar: ({
    message,
    severity,
  }: {
    message: string;
    severity: Severity;
  }) => {},
  closeSnackbar: (event: React.SyntheticEvent | Event, reason?: string) => {},
});
